import { provideAppInitializer, NgModule, inject, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Router } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
// import { NxWelcomeComponent } from './nx-welcome.component';
import { HomeComponent } from './home/home.component';
import { AboutComponent } from './about/about.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { TopNavigationComponent } from './top-navigation/top-navigation.component';
import { SiteFooterComponent } from './site-footer/site-footer.component';
import { HeroComponent } from './hero/hero.component';
import { NotImplementedComponent } from './not-implemented/not-implemented.component';
import { TeaByTypeComponent } from './tea-by-type/tea-by-type.component';
import { TeaByRegionComponent } from './tea-by-region/tea-by-region.component';
import { TeaItemComponent } from './tea-item/tea-item.component';
import { TeaByFlavourComponent } from './tea-by-flavour/tea-by-flavour.component';
import { TeaItemSummaryComponent } from './tea-item-summary/tea-item-summary.component';
import { MarkdownPipe } from './markdown.pipe';
import { HomeFeaturedComponent } from './home-featured/home-featured.component';
import { HomeCarouselComponent } from './home-carousel/home-carousel.component';
import { ImageModifierPipe } from './image-modifer.pipe';
import { TruncatedMarkdownComponent } from './truncated-markdown/truncated-markdown.component';
import { TeaItemListComponent } from './tea-item-list/tea-item-list.component';
import { CartViewComponent } from './cart-view/cart-view.component';
import { CheckoutComponent } from './checkout/checkout.component';
import { CheckoutStatusComponent } from './checkout-status/checkout-status.component';
import { OrderComponent } from './order/order.component';
import { ContactComponent } from './contact/contact.component';
import { ReactiveFormsModule } from '@angular/forms';
import { AlertComponent } from './alert/alert.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { OpenFeatureModule, BooleanFeatureFlagDirective, StringFeatureFlagDirective } from '@openfeature/angular-sdk';
import { growthBookClientProvider } from './growth-book.provider';
import { MaintenanceModeComponent } from './maintenance-mode/maintenance-mode.component';
import { ComingSoonComponent } from './coming-soon/coming-soon.component';
import { FeatureFlagContextService } from './feature-flag-context.service';
import { HttpConfigInterceptor } from './http-config.interceptor';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { environment } from '../environments/environment';
import { FrequentlyAskedQuestionsComponent } from './frequently-asked-questions/frequently-asked-questions.component';
import { BlogListComponent } from './blog-list/blog-list.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { VdpComponent } from './vdp/vdp.component';
import { TermsOfServiceComponent } from './terms-of-service/terms-of-service.component';
import { RefundsPolicyComponent } from './refunds-policy/refunds-policy.component';
import { SubscribeBlockComponent } from './subscribe-block/subscribe-block.component';
import * as Sentry from '@sentry/angular';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import { FeatureFlagsService } from './feature-flags.service';

@NgModule({ declarations: [
        AppComponent,
        // NxWelcomeComponent,
        HomeComponent,
        AboutComponent,
        NotFoundComponent,
        TopNavigationComponent,
        SiteFooterComponent,
        HeroComponent,
        NotImplementedComponent,
        TeaByTypeComponent,
        TeaByRegionComponent,
        TeaItemComponent,
        TeaByFlavourComponent,
        TeaItemSummaryComponent,
        MarkdownPipe,
        HomeFeaturedComponent,
        HomeCarouselComponent,
        ImageModifierPipe,
        TruncatedMarkdownComponent,
        TeaItemListComponent,
        CartViewComponent,
        CheckoutComponent,
        CheckoutStatusComponent,
        OrderComponent,
        ContactComponent,
        AlertComponent,
        MaintenanceModeComponent,
        ComingSoonComponent,
        FrequentlyAskedQuestionsComponent,
        BlogListComponent,
        PrivacyPolicyComponent,
        VdpComponent,
        TermsOfServiceComponent,
        RefundsPolicyComponent,
        SubscribeBlockComponent,
    ],
    exports: [
        RouterModule, 
        BooleanFeatureFlagDirective,
        StringFeatureFlagDirective,
    ],
    bootstrap: [AppComponent], 
    imports: [
        LoggerModule.forRoot({ 
            level: environment.LOGGER_LEVEL,
        }),
        ReactiveFormsModule,
        BrowserModule,
        RouterModule.forRoot(appRoutes, { initialNavigation: 'enabledBlocking' }),
        BrowserAnimationsModule,
        OpenFeatureModule.forRoot({
            provider: growthBookClientProvider
        }),
        BooleanFeatureFlagDirective,
        StringFeatureFlagDirective,
        ...(environment.OPTIMISATION_GA) ? [
            NgxGoogleAnalyticsModule.forRoot(environment.OPTIMISATION_GA),
            NgxGoogleAnalyticsRouterModule,
        ] : [],
    ],
    providers: [
        {
            provide: ErrorHandler,
            useFactory: async (featureFlags: FeatureFlagsService, logger: NGXLogger) => {
                await featureFlags.isReady();
                const disableSentry = await featureFlags.getBooleanValue('disable-sentry-fe', false);
                if (disableSentry) {
                    logger.warn('Sentry disabled');
                    return null;
                }
                logger.log('Sentry enabled');
                const disableSentryDialog = await featureFlags.getBooleanValue('disable-sentry-dialog', false);
                if (disableSentryDialog) {
                    logger.warn('Sentry dialog disabled');
                } else {
                    logger.log('Sentry dialog enabled');
                }
                return Sentry.createErrorHandler({
                    showDialog: !disableSentryDialog,
                });
            },
            deps: [FeatureFlagsService, NGXLogger]
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideHttpClient(withInterceptorsFromDi()),
        FeatureFlagContextService,
        provideAppInitializer(async () => {
            const contextService = inject(FeatureFlagContextService);
            const traceService = inject(Sentry.TraceService);
            const logger = inject(NGXLogger);
            if (!environment.OPTIMISATION_GA) {
                logger.warn('GA disabled');
            }
            await contextService['updateContext']();
            return Promise.resolve();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpConfigInterceptor,
            multi: true,
        }
    ],
})
export class AppModule {}
